import React from "react";
import type { FallbackProps } from "react-error-boundary";
import { FormattedMessage } from "react-intl";

import { isDevelopment } from "~/common/utils";
import { RemoteLogErrorBoundary } from "~/components/common/remote-log-error-boundary";
import { Button } from "~/components/ui/button";

import {
  PageFooterContainer,
  PageFooterFirstRow,
  PageFooterSecondRow,
  PageFooterVersion,
  PageFooterYear,
} from "./components/page_footer";
import { Portlet } from "./components/ui/portlet";
import { PublicLayout } from "./components/ui/public-layout";

export function AppErrorBoundary({ children }: { children: React.ReactNode }) {
  return (
    <RemoteLogErrorBoundary
      component="app"
      fallbackRender={(props) => <ErrorFallback {...props} />}
    >
      {children}
    </RemoteLogErrorBoundary>
  );
}

function ErrorFallback({ error, resetErrorBoundary }: FallbackProps) {
  const message = error instanceof Error ? error.message : String(error);
  const stack = error instanceof Error ? error.stack : undefined;

  return (
    <PublicLayout>
      <Portlet as="main" className="tw-mb-[5px] max-sm:tw-mt-5">
        <div
          role="alert"
          className="tw-flex tw-h-full tw-flex-col tw-items-center tw-justify-center tw-p-6 tw-text-red-700 sm:tw-p-8"
        >
          <div className="tw-space-y-4 tw-text-center">
            <h2 className="tw-text-2xl tw-font-bold">
              <FormattedMessage id="error_boundary.message" />.
            </h2>

            <p className="tw-leading-10">
              <FormattedMessage id="error_boundary.message_1" />
              <br />
              <FormattedMessage id="error_boundary.message_2" />.
            </p>

            {isDevelopment ? (
              <div className="tw-rounded-md tw-bg-red-100 tw-p-4 tw-text-left">
                <p className="tw-font-mono tw-text-sm tw-text-red-900">{message}</p>

                {stack ? (
                  <pre className="tw-mt-2 tw-whitespace-pre-wrap tw-break-all tw-text-xs">
                    {stack}
                  </pre>
                ) : null}
              </div>
            ) : null}

            <Button variant="danger" rounded size="xl" onClick={resetErrorBoundary}>
              <FormattedMessage id="error_boundary.reset" />
            </Button>
          </div>
        </div>
      </Portlet>

      <PageFooterContainer center>
        <PageFooterFirstRow>
          <span>
            <PageFooterYear /> <FormattedMessage id="page.footer.header" />
          </span>

          <a href="https://3d-smile.ru" target="_blank" rel="noopener noreferrer">
            <FormattedMessage id="page.footer.content" />
          </a>
        </PageFooterFirstRow>

        <PageFooterSecondRow>
          <a href="https://3d-smile.ru/policy" target="_blank" rel="noopener noreferrer">
            <FormattedMessage id="Privacy" />
          </a>

          <a href="/contacts" target="_blank" rel="noopener noreferrer">
            <FormattedMessage id="page.footer.contacts" />
          </a>

          <PageFooterVersion />
        </PageFooterSecondRow>
      </PageFooterContainer>
    </PublicLayout>
  );
}
